import { PageHeader } from 'components/page-header';
import { Helmet } from 'react-helmet-async';
import { pageTitle } from 'utils/page-title';
import { useStateTaxesSearchAtom } from './atoms';
import { useAtom, useSetAtom } from 'jotai';
import {
  Button,
  DataTableSkeleton,
  InlineNotification,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  ToastNotification,
} from '@carbon/react';
import { StateTaxesQueryResponse } from 'types/api-responses';
import { parseMomentUtc, US_DATE_FORMAT } from 'utils/dates';
import CommentSvg from 'images/srp-comment.svg';
import styles from './state-taxes.module.scss';
import { DECIMAL_FORMATTER_THREE_FRACTIONS } from 'lib/ui';
import { Edit, AddComment } from '@carbon/react/icons';
import { useState } from 'react';
import { EditStateTaxModal } from './edit-state-tax-modal';
import {
  ActivityFeedPanel,
  isActivityFeedPanelOpenAtom,
} from 'components/activity-feed-panel';

export const StateTaxesPage = () => {
  const { searchAtom } = useStateTaxesSearchAtom();
  const [searchResult, setSearchResult] = useAtom(searchAtom);
  const [selectedStateTax, setSelectedStateTax] =
    useState<StateTaxesQueryResponse | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [toast, setToast] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [updateFailedError, setUpdateFailedError] = useState(false);
  const setActivityFeedOpen = useSetAtom(isActivityFeedPanelOpenAtom);
  const [activityStateTax, setActivityStateTax] =
    useState<StateTaxesQueryResponse | null>(null);

  const handleActivityClick = (stateTax: StateTaxesQueryResponse) => {
    setActivityStateTax(stateTax);
    setActivityFeedOpen(true);
  };

  const handleEditClick = (stateTax: StateTaxesQueryResponse) => {
    setSelectedStateTax(stateTax);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setUpdateFailedError(false);
  };

  const handleSaved = () => {
    setIsModalOpen(false);
    setToast({ type: 'success', message: 'State tax updated successfully' });
    // Refresh the data by passing a function that returns the search params
    setSearchResult(() => ({})); // or pass your specific search params if needed
  };

  const closeToast = () => {
    setToast(null);
    return false;
  };

  const headers: Array<{
    key: keyof StateTaxesQueryResponse | 'actions';
    header: string;
    render?: (
      value: string | number,
      row?: StateTaxesQueryResponse
    ) => React.ReactNode;
    width?: number;
    align?: 'left' | 'right' | 'center';
  }> = [
    {
      key: 'stateAbbreviation',
      header: 'State',
      width: 25,
      align: 'left',
    },
    {
      key: 'oilTaxPercentage',
      header: 'Oil Tax (%)',
      align: 'right',
      render: (value: number | string) => {
        return (
          <>
            {Number.isFinite(value)
              ? `${DECIMAL_FORMATTER_THREE_FRACTIONS.format(Number(value))}`
              : '-'}
          </>
        );
      },
      width: 12,
    },
    {
      key: 'gasTaxPercentage',
      header: 'Gas Tax (%)',
      align: 'right',
      render: (value: number | string) => {
        return (
          <>
            {Number.isFinite(value)
              ? `${DECIMAL_FORMATTER_THREE_FRACTIONS.format(Number(value))}`
              : '-'}
          </>
        );
      },
      width: 12,
    },
    {
      key: 'nglTaxPercentage',
      header: 'NGL Tax (%)',
      align: 'right',
      render: (value: number | string) => {
        return (
          <>
            {Number.isFinite(value)
              ? `${DECIMAL_FORMATTER_THREE_FRACTIONS.format(Number(value))}`
              : '-'}
          </>
        );
      },
      width: 12,
    },
    {
      key: 'adValoremTaxPercentage',
      header: 'Ad Valorem Tax (%)',
      align: 'right',
      render: (value: number | string) => {
        return (
          <>
            {Number.isFinite(value)
              ? `${DECIMAL_FORMATTER_THREE_FRACTIONS.format(Number(value))}`
              : '-'}
          </>
        );
      },
      width: 12,
    },
    {
      key: 'createdAt',
      header: 'Created At',
      align: 'center',
      render: (value: string | number) => {
        return (
          <>{value ? parseMomentUtc(value).format(US_DATE_FORMAT) : '-'}</>
        );
      },
    },
    {
      key: 'updatedAt',
      header: 'Updated At',
      align: 'center',
      render: (value: string | number) => {
        return (
          <>{value ? parseMomentUtc(value).format(US_DATE_FORMAT) : '-'}</>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      align: 'center',
      width: 0,
      render: (_, row) => {
        return (
          <div className={styles.actionsCell}>
            <Button
              hasIconOnly
              renderIcon={AddComment}
              iconDescription="Activity"
              onClick={() => row && handleActivityClick(row)}
              size="sm"
              kind="ghost"
            />
            <Button
              hasIconOnly
              renderIcon={Edit}
              iconDescription="Edit"
              onClick={() => row && handleEditClick(row)}
              size="sm"
              kind="ghost"
            />
          </div>
        );
      },
    },
  ];

  const errorContent = (
    <tr>
      <td colSpan={headers.length}>
        <div className={styles.errorContainer}>
          <img src={CommentSvg} />
          <h3>Data Error</h3>
          <p className={styles.errorMessage}>
            Please refresh the page. If error persists, reach out to
            #insights-oncall
          </p>
          <Button onClick={() => window.location.reload()}>Refresh</Button>
        </div>
        <div className={styles.notificationWrapper}>
          <InlineNotification
            kind="error"
            title="Data Error"
            subtitle="Please refresh the page."
          />
        </div>
      </td>
    </tr>
  );

  return (
    <>
      <Helmet>
        <title>{pageTitle('State Taxes')}</title>
      </Helmet>
      <PageHeader title="State Taxes" breadcrumbs={[]} actions={<></>} />
      {searchResult.loading ? (
        <DataTableSkeleton
          data-testid="data-table-skeleton"
          columnCount={7}
          rowCount={10}
          showHeader={false}
          showToolbar={false}
        />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableHeader
                    key={header.key}
                    style={{
                      width: `${header.width}%`,
                      textAlign: header.align,
                    }}
                  >
                    {header.header}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {searchResult.error
                ? errorContent
                : searchResult.data?.results?.map((result) => (
                    <TableRow key={result.stateAbbreviation}>
                      {headers.map((header) => (
                        <TableCell
                          key={header.key}
                          style={{
                            width: `${header.width}%`,
                            textAlign: header.align,
                          }}
                        >
                          {header.render
                            ? header.render(
                                result[
                                  header.key as keyof StateTaxesQueryResponse
                                ],
                                result
                              )
                            : result[
                                header.key as keyof StateTaxesQueryResponse
                              ]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Modal */}
      {selectedStateTax && (
        <EditStateTaxModal
          open={isModalOpen}
          stateTax={selectedStateTax}
          onClose={handleCloseModal}
          onSaved={handleSaved}
          setUpdateFailedError={setUpdateFailedError}
          updateFailedError={updateFailedError}
        />
      )}

      {/* Activity Feed Panel */}
      {activityStateTax && (
        <ActivityFeedPanel
          modelType="StateAssumption"
          modelId={activityStateTax.id}
          title="Activity"
          subtitle={`${activityStateTax.stateAbbreviation} State Tax`}
          commentsHref={`/state_taxes/${activityStateTax.id}/comments`}
        />
      )}

      {/* Toast Notifications */}
      {toast && (
        <div className={styles.toastNotificationWrapper}>
          <ToastNotification
            kind={toast.type}
            title={toast.type === 'success' ? 'Success' : 'Error'}
            subtitle={toast.message}
            timeout={5000}
            onClose={closeToast}
          />
        </div>
      )}
    </>
  );
};
